import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface Receipt {
  id: string;
  number: string;
  date: string;
  time: string;
  customer?: string;
  items: number;
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod: string;
  status: "completed" | "refunded" | "voided";
}

const MOCK_RECEIPTS: Receipt[] = [
  {
    id: "1",
    number: "REC-001234",
    date: "2024-01-15",
    time: "14:32",
    customer: "Juan Pérez",
    items: 5,
    subtotal: 156.50,
    tax: 25.04,
    total: 181.54,
    paymentMethod: "card",
    status: "completed",
  },
  {
    id: "2",
    number: "REC-001235",
    date: "2024-01-15",
    time: "15:45",
    items: 3,
    subtotal: 89.99,
    tax: 14.40,
    total: 104.39,
    paymentMethod: "cash",
    status: "completed",
  },
  {
    id: "3",
    number: "REC-001236",
    date: "2024-01-15",
    time: "16:20",
    customer: "María García",
    items: 2,
    subtotal: 245.00,
    tax: 39.20,
    total: 284.20,
    paymentMethod: "transfer",
    status: "refunded",
  },
  {
    id: "4",
    number: "REC-001237",
    date: "2024-01-14",
    time: "10:15",
    items: 1,
    subtotal: 35.75,
    tax: 5.72,
    total: 41.47,
    paymentMethod: "cash",
    status: "voided",
  },
  {
    id: "5",
    number: "REC-001238",
    date: "2024-01-14",
    time: "11:30",
    customer: "Carlos López",
    items: 8,
    subtotal: 420.00,
    tax: 67.20,
    total: 487.20,
    paymentMethod: "card",
    status: "completed",
  },
];

export default function ReceiptsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const getStatusColor = (status: Receipt["status"]) => {
    switch (status) {
      case "completed":
        return "bg-primary-500/10 text-primary-500 border-primary-500/30";
      case "refunded":
        return "bg-orange-500/10 text-orange-500 border-orange-500/30";
      case "voided":
        return "bg-red-500/10 text-red-500 border-red-500/30";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500/30";
    }
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case "cash":
        return "ri-money-dollar-circle-line";
      case "card":
        return "ri-bank-card-line";
      case "transfer":
        return "ri-exchange-line";
      default:
        return "ri-wallet-3-line";
    }
  };

  const filteredReceipts = MOCK_RECEIPTS.filter((receipt) => {
    const matchesSearch = receipt.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.customer?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || receipt.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalSales = filteredReceipts
    .filter((r) => r.status === "completed")
    .reduce((acc, r) => acc + r.total, 0);

  return (
    <div className="h-full flex">
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
        <div className="mb-6">
          <h1 className={`text-2xl font-bold ${themeClasses.text} mb-4`}>
            <i className="ri-receipt-line mr-2 text-primary-500"></i>
            {t("posReceipts.title")}
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}></i>
              <input
                type="text"
                placeholder={t("posReceipts.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>

            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                className={`px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                className={`px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            {["all", "completed", "refunded", "voided"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  filterStatus === status
                    ? "bg-primary-500 text-gray-900 font-medium"
                    : `${themeClasses.cardBg} ${themeClasses.text} border ${themeClasses.border}`
                }`}
              >
                {t(`posReceipts.status.${status}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posReceipts.totalReceipts")}</p>
            <p className={`text-2xl font-bold ${themeClasses.text}`}>{filteredReceipts.length}</p>
          </div>
          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posReceipts.totalSales")}</p>
            <p className="text-2xl font-bold text-primary-500">${totalSales.toFixed(2)}</p>
          </div>
          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posReceipts.averageTicket")}</p>
            <p className={`text-2xl font-bold ${themeClasses.text}`}>
              ${filteredReceipts.length > 0 ? (totalSales / filteredReceipts.filter(r => r.status === "completed").length).toFixed(2) : "0.00"}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {filteredReceipts.map((receipt) => (
            <button
              key={receipt.id}
              onClick={() => setSelectedReceipt(receipt)}
              className={`w-full ${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border} text-left hover:border-primary-500 transition-all ${
                selectedReceipt?.id === receipt.id ? "border-primary-500" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${themeClasses.bg} flex items-center justify-center`}>
                    <i className={`${getPaymentIcon(receipt.paymentMethod)} text-xl ${themeClasses.text}`}></i>
                  </div>
                  <div>
                    <p className={`font-bold ${themeClasses.text}`}>{receipt.number}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      {receipt.date} • {receipt.time}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-lg ${themeClasses.text}`}>${receipt.total.toFixed(2)}</p>
                  <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(receipt.status)}`}>
                    {t(`posReceipts.status.${receipt.status}`)}
                  </span>
                </div>
              </div>
              {receipt.customer && (
                <p className={`text-sm ${themeClasses.textSecondary}`}>
                  <i className="ri-user-line mr-1"></i>
                  {receipt.customer}
                </p>
              )}
              <p className={`text-sm ${themeClasses.textSecondary}`}>
                {receipt.items} {t("posTerminal.items")}
              </p>
            </button>
          ))}
        </div>
      </div>

      {selectedReceipt && (
        <div className={`w-80 lg:w-96 ${themeClasses.bg} border-l ${themeClasses.border} flex flex-col`}>
          <div className={`p-4 border-b ${themeClasses.border}`}>
            <div className="flex items-center justify-between">
              <h2 className={`text-lg font-bold ${themeClasses.text}`}>{t("posReceipts.receiptDetails")}</h2>
              <button
                onClick={() => setSelectedReceipt(null)}
                className={`p-2 rounded-lg ${themeClasses.cardBg}`}
              >
                <i className={`ri-close-line ${themeClasses.text}`}></i>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="text-center mb-6">
              <p className={`text-2xl font-bold ${themeClasses.text} mb-1`}>{selectedReceipt.number}</p>
              <p className={themeClasses.textSecondary}>
                {selectedReceipt.date} • {selectedReceipt.time}
              </p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(selectedReceipt.status)}`}>
                {t(`posReceipts.status.${selectedReceipt.status}`)}
              </span>
            </div>

            {selectedReceipt.customer && (
              <div className={`p-3 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border} mb-4`}>
                <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posReceipts.customer")}</p>
                <p className={`font-medium ${themeClasses.text}`}>{selectedReceipt.customer}</p>
              </div>
            )}

            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t("posReceipts.items")}</span>
                <span className={themeClasses.text}>{selectedReceipt.items}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t("posTerminal.subtotal")}</span>
                <span className={themeClasses.text}>${selectedReceipt.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className={themeClasses.textSecondary}>{t("posTerminal.tax")}</span>
                <span className={themeClasses.text}>${selectedReceipt.tax.toFixed(2)}</span>
              </div>
              <div className={`flex justify-between text-lg font-bold border-t ${themeClasses.border} pt-3`}>
                <span className={themeClasses.text}>{t("posTerminal.total")}</span>
                <span className="text-primary-500">${selectedReceipt.total.toFixed(2)}</span>
              </div>
            </div>

            <div className={`p-3 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>{t("posReceipts.paymentMethod")}</p>
              <div className="flex items-center gap-2">
                <i className={`${getPaymentIcon(selectedReceipt.paymentMethod)} ${themeClasses.text}`}></i>
                <span className={`font-medium ${themeClasses.text} capitalize`}>{selectedReceipt.paymentMethod}</span>
              </div>
            </div>
          </div>

          <div className={`p-4 border-t ${themeClasses.border} space-y-2`}>
            <button className="w-full p-3 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center justify-center gap-2">
              <i className="ri-printer-line"></i>
              {t("posReceipts.printReceipt")}
            </button>
            <button className={`w-full p-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center justify-center gap-2`}>
              <i className="ri-mail-line"></i>
              {t("posReceipts.sendByEmail")}
            </button>
            {selectedReceipt.status === "completed" && (
              <button className="w-full p-3 rounded-xl border border-red-500 text-red-500 font-medium flex items-center justify-center gap-2">
                <i className="ri-refund-2-line"></i>
                {t("posReceipts.processRefund")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
